import PatternStore from './modules/pouchdb';
import { getAsyncInjectors } from 'utils/asyncInjectors';
import Auth from './modules/Auth';

const errorLoading = (err) => {
  console.error('Dynamic page loading failed', err); // eslint-disable-line no-console
};

const loadModule = (cb) => (componentModule) => {
  cb(null, componentModule.default);
};

export default function createRoutes(store) {
  // Create reusable async injectors using getAsyncInjectors factory
  const { injectReducer, injectSagas } = getAsyncInjectors(store); // eslint-disable-line no-unused-vars

  return [
    {
      path: '/app',
      name: '/',
      onEnter: () => {
        if (!Auth.hasCookie()) {
          window.location.href = '/app/login';
        }
      },
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('containers/HomePage/reducer'),
          import('containers/ProfilePage/reducer'),
          import('containers/HomePage'),
          import('containers/HomePage/sagas'),
          import('containers/ProfilePage/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([reducer, userReducer, component, sagas, profileSagas]) => {
          injectReducer('dashboard', reducer.default);
          injectReducer('user', userReducer.default);
          injectSagas(sagas.default);
          injectSagas(profileSagas.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: '/app/login',
      name: 'login',
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('containers/Login'),
          import('containers/ProfilePage/reducer'),
          import('containers/Login/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component, reducer, sagas]) => {
          injectReducer('user', reducer.default);
          injectSagas(sagas.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: '/app/login/:email',
      name: 'login',
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('containers/Login'),
          import('containers/ProfilePage/reducer'),
          import('containers/Login/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component, reducer, sagas]) => {
          injectReducer('user', reducer.default);
          injectSagas(sagas.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: '/app/signup',
      name: 'signup',
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('containers/Signup'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component]) => {
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    },
    {
      path: '/app/profile',
      name: 'profile',
      onEnter: (nextState, replace) => {
        if (!Auth.hasCookie()) {
          replace('/app/login');
        }
      },
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('containers/ProfilePage'),
          import('containers/ProfilePage/reducer'),
          import('containers/ProfilePage/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component, reducer, sagas]) => {
          injectReducer('user', reducer.default);
          injectSagas(sagas.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    }, {
      path: '/app/register/:token',
      name: 'register',
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('containers/Register'),
          import('containers/ProfilePage/reducer'),
          import('containers/Register/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component, reducer, sagas]) => {
          injectReducer('user', reducer.default);
          injectSagas(sagas.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    }, {
      path: '/app/register',
      name: 'nregister',
      onEnter: (nextState, replace) => {
        replace('/app/');
      },
    }, {
      path: '/app/reset_password',
      name: 'reset_password',
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('containers/ResetPassword'),
          import('containers/ResetPassword/reducer'),
          import('containers/ResetPassword/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component, reducer, sagas]) => {
          injectReducer('reset', reducer.default);
          injectSagas(sagas.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    }, {
      path: '/app/reset_password/:token',
      name: 'reset_password',
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('containers/ResetPassword'),
          import('containers/ResetPassword/reducer'),
          import('containers/ProfilePage/reducer'),
          import('containers/ResetPassword/sagas'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component, reducer, userReducer, sagas]) => {
          injectReducer('reset', reducer.default);
          injectReducer('user', userReducer.default);
          injectSagas(sagas.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    }, {
      // DISABLED (chat and shared work, relaunching as a simpler platform): was '/app/editor/:patternId(/:ownerId)'
      path: '/app/editor/:patternId',
      name: 'editor',
      onEnter: (nextState, replace) => {
        if (!Auth.hasCookie()) {
          replace('/app/login');
        }
      },
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('commons/reducers'),
          import('containers/ProfilePage/reducer'),
          import('containers/HomePage/reducer'),
          import('containers/Editor/sagas'),
          import('containers/ProfilePage/sagas'),
          import('containers/Editor'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([patternReducer, userReducer, homeReducer, editorSagas, userSagas, component]) => {
          injectReducer('editor', patternReducer.default);
          injectReducer('dashboard', homeReducer.default);
          injectReducer('user', userReducer.default);
          injectSagas(editorSagas.default);
          injectSagas(userSagas.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    }, {
      path: '/app/editor',
      name: 'neditor',
      onEnter: (nextState, replace) => {
        replace('/app/');
      },
    }, {
      path: '/app/create-pattern(/:patternId)',
      name: 'CreatePattern',
      onEnter: () => {
        if (!Auth.hasCookie()) {
          window.location.href = '/app/login';
        }
      },
      getComponent(nextState, cb) {
        const importModules = Promise.all([
          import('pages/CreatePattern'),
          import('pages/CreatePattern/reducer'),
        ]);

        const renderRoute = loadModule(cb);

        importModules.then(([component, reducer]) => {
          injectReducer('user', reducer.default);
          renderRoute(component);
        });

        importModules.catch(errorLoading);
      },
    }, {
      path: '/app/logout',
      name: '/app/logout',
      onEnter: () => {
        // Send any edits still waiting, then clear this user's local copy from the browser.
        const user = Auth.getUser();
        PatternStore.signOut(user && user._id).finally(() => {
          Auth.deauthenticateUser();
          window.location.replace('/');
        });
      },
    }, {
      path: '*',
      name: 'notfound',
      getComponent(nextState, cb) {
        import('containers/NotFoundPage')
          .then(loadModule(cb))
          .catch(errorLoading);
      },
    },
  ];
}
